/**
 * Grafana Faro initialization for the web app frontend.
 *
 * Guarded by VITE_FARO_COLLECTOR_URL. Coexists with Sentry.
 * See infra-observability/docs/instrumentation-guide.md
 */

import {
  faro,
  FetchTransport,
  getWebInstrumentations,
  initializeFaro,
  createSession,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import {
  buildFaroAppConfig,
  buildOtelFaroFetchTransportOptions,
} from '@novaquantis/nq-observability/frontend';

const collectorUrl = import.meta.env.VITE_FARO_COLLECTOR_URL as
  | string
  | undefined;
const apiKey = import.meta.env.VITE_OTEL_API_KEY as string | undefined;

if (collectorUrl) {
  initializeFaro({
    transports: [
      new FetchTransport(
        buildOtelFaroFetchTransportOptions(collectorUrl, apiKey),
      ),
    ],
    app: buildFaroAppConfig({
      appName: 'novaquantis-web',
      version: import.meta.env.VITE_APP_VERSION as string | undefined,
      environment: import.meta.env.MODE,
      serviceName: 'web-app-frontend',
    }),
    instrumentations: [
      ...getWebInstrumentations(),
      new TracingInstrumentation(),
    ],
  });
  // eslint-disable-next-line no-console
  console.log('[faro] initialized for novaquantis-web');
}

const FARO_SESSION_STORAGE_KEY = 'nq_faro_session_id';

function getOrCreateFaroSessionId() {
  if (typeof window === 'undefined') return 'server';

  const existing = window.sessionStorage.getItem(FARO_SESSION_STORAGE_KEY);
  if (existing) return existing;

  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}`;
  window.sessionStorage.setItem(FARO_SESSION_STORAGE_KEY, generated);
  return generated;
}

export function getFaroSessionId() {
  return getOrCreateFaroSessionId();
}

export type FaroUserContext = {
  id: string;
  username?: string;
  email?: string;
  role?: string;
};

export function setFaroUserContext(user: FaroUserContext) {
  faro.api?.setUser({
    id: user.id,
    username: user.username,
    email: user.email,
    attributes: {
      role: user.role ?? 'unknown',
      source: 'web-app-frontend',
    },
  });
  faro.api?.setSession(
    createSession({
      id: getOrCreateFaroSessionId(),
      userId: user.id,
      userEmail: user.email ?? '',
      userRole: user.role ?? '',
    }),
  );
}

export function clearFaroUserContext() {
  faro.api?.resetUser();
  faro.api?.resetSession();
}

export function trackFaroEvent(
  name: string,
  attributes: Record<string, string | number | boolean | undefined>,
  domain = 'novaquantis.user_action',
) {
  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) continue;
    normalized[key] = String(value);
  }
  faro.api?.pushEvent(name, normalized, domain, { skipDedupe: true });
}

export { faro } from '@grafana/faro-web-sdk';
